/**
 * Central home for every user-facing sync string.
 *
 * Rules encoded here — do not violate them in ad-hoc strings elsewhere:
 * - UI copy says "collection", never "workspace".
 * - Never surface "Tour", "event", or byte sizes to users; queued work is "changes" or "items".
 * - "Sync" is the user-facing verb; raw error/exception text belongs only in "Details for support".
 * - Waiting (offline, retrying) is calm and factual — queued work is safe on this device.
 */

export function pluralizeItems(count: number): string {
  return count === 1 ? '1 item' : `${count} items`
}

export function pluralizeChanges(count: number): string {
  return count === 1 ? '1 change' : `${count} changes`
}

/** Status headline shown in the sync dialog. */
export function statusHeadline(
  status: 'idle' | 'syncing' | 'success' | 'error' | 'offline',
  unsyncedCount: number,
): string {
  switch (status) {
    case 'syncing':
      return 'Syncing your changes…'
    case 'error':
      return 'Sync problem'
    case 'offline':
      return unsyncedCount > 0
        ? `Offline — ${pluralizeChanges(unsyncedCount)} will sync later`
        : 'Offline'
    case 'success':
      return 'Everything synced'
    default:
      return unsyncedCount > 0
        ? `${pluralizeChanges(unsyncedCount)} waiting to sync`
        : 'Everything synced'
  }
}

/**
 * Map a raw sync error to a human sentence. The raw string stays available in
 * "Details for support"; users only ever see these.
 */
export function humanizeSyncError(rawMessage: string | null, isAuthenticated: boolean): string {
  const raw = (rawMessage ?? '').toLowerCase()

  if (
    !isAuthenticated ||
    raw.includes('not logged in') ||
    raw.includes('unauthorized') ||
    raw.includes('401') ||
    raw.includes('403')
  ) {
    return 'You are signed out. Sign in to finish syncing your changes.'
  }
  if (raw.includes('forced offline')) {
    return 'Offline mode is turned on. Your changes are saved on this device and will sync once you turn it off.'
  }
  if (raw.includes('offline') || raw.includes('network')) {
    return 'No connection right now. Your changes are saved on this device and will sync automatically.'
  }
  if (raw.includes('timeout') || raw.includes('timed out')) {
    return 'The connection is slow or unstable. Syncing will keep retrying automatically.'
  }
  if (
    raw.includes('rejected') ||
    raw.includes('validation') ||
    raw.includes('400') ||
    raw.includes('422')
  ) {
    return "The server couldn't accept one of your changes. Syncing will retry automatically."
  }
  return 'Syncing hit a problem. Your changes are saved on this device and syncing will retry automatically.'
}

/** Ambient retry label ("Retrying in ~30s (attempt 2 of 5)"). */
export function retryLabel(
  secondsUntilRetry: number | null,
  attempt: number,
  maxAttempts: number,
): string {
  if (secondsUntilRetry === null) {
    return 'Sync paused — will retry soon'
  }
  const seconds = Math.max(1, Math.round(secondsUntilRetry))
  return `Retrying in ~${seconds}s (attempt ${attempt} of ${maxAttempts})`
}

/**
 * Leave-guard copy. Two very different consequences share one guard today:
 * - safe leave (tab change, reload): the queue persists in IndexedDB and resumes — say so.
 * - destructive leave (switching/creating a collection wipes the local queue): be explicit.
 */
export function leaveGuardCopy(
  unsyncedCount: number,
  destructive: boolean,
): { title: string; message: string } {
  const items = pluralizeItems(unsyncedCount)
  if (destructive) {
    return {
      title: 'Unsynced changes will be deleted',
      message:
        `You have ${items} that haven't synced yet. If you continue, they will be deleted from this device and cannot be recovered. ` +
        'Wait for syncing to finish (recommended), or continue and discard them.',
    }
  }
  return {
    title: 'Still syncing',
    message:
      `Still syncing ${items}. They're stored safely on this device and will finish syncing next time you open the app here. ` +
      'You can leave now or wait for syncing to finish.',
  }
}

/** Success confirmation after a GPX/FIT import — honest about the background sync. */
export function importSuccessMessage(importedCount: number, isOnline: boolean): string {
  const what = importedCount === 1 ? 'Track imported' : `${importedCount} tracks imported`
  return isOnline
    ? `${what} — syncing in the background.`
    : `${what} — saved on this device, will sync when you're back online.`
}

/** Human labels for the activity view's item groups (sync-feedback.md §4.2/§5). */
export const activityLabels = {
  photos(count: number, collectionName?: string): string {
    const what = count === 1 ? '1 photo' : `${count} photos`
    return collectionName ? `${what} → '${collectionName}'` : what
  },
  photoChanges(count: number): string {
    return count === 1 ? 'A photo change' : `Photo changes (${count})`
  },
  track(name?: string): string {
    return name?.trim() ? `Track '${name}'` : 'A new track'
  },
  places(count: number): string {
    return count === 1 ? '1 place' : `${count} places`
  },
  collectionChanges(collectionName?: string): string {
    return collectionName ? `Changes to '${collectionName}'` : 'Collection changes'
  },
  newCollection(collectionName?: string): string {
    return collectionName ? `New collection '${collectionName}'` : 'A new collection'
  },
  routeChanges(count: number): string {
    return count === 1 ? 'A route change' : `Route changes (${count})`
  },
  otherChanges(count: number): string {
    return count === 1 ? 'Another change' : `Other changes (${count})`
  },
} as const

/** Status chip copy for activity items. */
export const activityStatusLabels = {
  syncing: 'syncing…',
  waiting: 'waiting',
  problem: "can't sync yet",
  failed: 'rejected',
} as const

/** Needs-attention section (quarantined items) — headline + per-item explainer. */
export function quarantineHeadline(count: number): string {
  return count === 1 ? "1 change couldn't be synced" : `${count} changes couldn't be synced`
}

export const quarantineExplainer =
  'The server rejected these changes, so they were set aside — everything else keeps syncing. ' +
  'You can try them again or remove them from this device.'

export function discardConfirmCopy(label: string): { title: string; message: string } {
  return {
    title: 'Remove this change?',
    message:
      `"${label}" will be removed from this device and cannot be recovered. ` +
      'Anything already synced stays untouched.',
  }
}

/**
 * Feedback for a manual "Sync now" that failed immediately — without it, a fast server
 * rejection leaves the dialog looking untouched and the button appears dead.
 */
export const manualRetryFailedMessage =
  'Tried again just now — the same problem occurred. Your changes stay safe on this device.'

/**
 * Shown instead of the "will retry automatically" reassurance once a full retry cycle has
 * failed — at that point the promise is no longer credible and the user needs options.
 */
export const persistentFailureMessage =
  'The server keeps rejecting one of your changes, so retrying may not fix this. ' +
  'Your work is safe on this device. Copy the report below and send it to support — ' +
  'syncing will keep retrying in the background meanwhile.'

/** One-time toasts on entering an actionable state (sync-feedback.md §4.4). */
export const syncToasts = {
  needsAttention: 'Some changes couldn’t be synced yet. Tap the sync icon to see details.',
  signedOut: 'You are signed out. Sign in to finish syncing your changes.',
  savedOffline: 'Saved on this device — will sync when you’re back online.',
  photoNotSaved: 'Photo NOT saved — free up space and try again.',
  itemNotSaved: 'Change NOT saved — free up space and try again.',
  photosNeedCollection:
    'Photos live inside a collection. Open a collection first, then add your photos.',
} as const
